import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRightIcon,
  XMarkIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface DashboardTourProps {
  onComplete: () => void;
  onTourModeChange?: (isInTourMode: boolean) => void;
  onTriggerVwapDemo?: () => void;
}

interface TourStep {
  id: number;
  title: string;
  description: string;
  targetElement?: string;
  icon: React.ReactNode;
  position: { top?: string; left?: string; right?: string; bottom?: string };
}

const DashboardTour: React.FC<DashboardTourProps> = ({ onComplete, onTourModeChange, onTriggerVwapDemo }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightBox, setHighlightBox] = useState<DOMRect | null>(null);
  
  const tourSteps: TourStep[] = [
    {
      id: 1,
      title: "Welcome to Your Dashboard!",
      description: "Let's take a quick tour of your equity facility dashboard. You can monitor your eligibility, execute trades, and manage your facility all in one place.",
      icon: <ChartBarIcon className="h-6 w-6" />,
      position: { top: '50%', left: '50%' }
    },
    {
      id: 2,
      title: "Daily Eligibility Monitoring",
      description: "This section shows your daily eligibility, which is automatically updated at 4:30 PM EST each trading day. You can see your available capacity and trading metrics.",
      targetElement: '#eligibility-section',
      icon: <ChartBarIcon className="h-6 w-6" />,
      position: { top: '200px', left: '50%' }
    },
    {
      id: 3,
      title: "Exercise Notices",
      description: "Here you can view and execute various exercise notices with just a click. Each notice type has different terms and settlement periods.",
      targetElement: '#exercise-notices',
      icon: <DocumentTextIcon className="h-6 w-6" />,
      position: { top: '400px', left: '50%' }
    },
    {
      id: 4,
      title: "VWAP Purchase Notice Demo",
      description: "Now let's see how easy it is to execute a VWAP Purchase Notice. Click 'Start Demo' below to see the process.",
      targetElement: '#vwap-notice',
      icon: <ClockIcon className="h-6 w-6" />,
      position: { top: '500px', left: '50%' }
    }
  ];

  const currentTourStep = tourSteps[currentStep];

  // Update highlight box position when step changes
  useEffect(() => {
    const updateHighlight = () => {
      if (currentTourStep && currentTourStep.targetElement) {
        const element = document.querySelector(currentTourStep.targetElement);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightBox(rect);
        }
      } else {
        setHighlightBox(null);
      }
    };

    if (currentTourStep && currentTourStep.targetElement) {
      const element = document.querySelector(currentTourStep.targetElement);
      if (element) {
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Wait for scroll to complete, then get the new position
        setTimeout(updateHighlight, 500);
      }
    } else {
      setHighlightBox(null);
    }

    // Update on window resize
    window.addEventListener('resize', updateHighlight);
    window.addEventListener('scroll', updateHighlight);
    
    return () => {
      window.removeEventListener('resize', updateHighlight);
      window.removeEventListener('scroll', updateHighlight);
    };
  }, [currentStep, currentTourStep]);

  const handleNext = () => {
    if (currentStep === 3) {
      // On step 4 (VWAP demo), enable tour mode and trigger demo
      onTourModeChange?.(true);
      // Trigger VWAP demo immediately
      onTriggerVwapDemo?.();
      // Hide the tour overlay
      setCurrentStep(-1);
    } else if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // This shouldn't happen but just in case
      onTourModeChange?.(false);
      onComplete();
    }
  };

  const handleSkip = () => {
    onTourModeChange?.(false);
    onComplete();
  };

  // If we're at step -1 (hidden), don't render anything
  if (currentStep === -1 || !currentTourStep || currentStep >= tourSteps.length) {
    return null;
  }

  return (
    <>
      {/* Spotlight overlay - only dim non-highlighted areas */}
      <div className="fixed inset-0 z-[9998] pointer-events-none">
        {/* Dark overlay with cutout */}
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {highlightBox && (
                <rect
                  x={highlightBox.left - 10}
                  y={highlightBox.top - 10}
                  width={highlightBox.width + 20}
                  height={highlightBox.height + 20}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect 
            x="0" 
            y="0" 
            width="100%" 
            height="100%" 
            fill="rgba(0, 0, 0, 0.7)" 
            mask="url(#spotlight-mask)"
          />
        </svg>
        
        {/* Highlight border */}
        {highlightBox && (
          <>
            <div
              className="absolute border-2 border-primary-500 rounded-lg shadow-[0_0_0_4px_rgba(59,130,246,0.1)] animate-pulse"
              style={{
                left: highlightBox.left - 10,
                top: highlightBox.top - 10,
                width: highlightBox.width + 20,
                height: highlightBox.height + 20,
                transition: 'all 0.3s ease-in-out'
              }}
            />
            {/* Pointer arrow */}
            <div
              className="absolute animate-bounce"
              style={{
                left: highlightBox.left + highlightBox.width / 2 - 20,
                top: highlightBox.top - 50,
              }}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M20 35L12 25L17 25L17 5L23 5L23 25L28 25L20 35Z" fill="#3B82F6" />
              </svg>
            </div>
          </>
        )}
      </div>
      
      {/* Tour tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed z-[9999] bg-white rounded-xl shadow-2xl p-6 max-w-md pointer-events-auto"
          style={{
            ...currentTourStep.position,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                {currentTourStep.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {currentTourStep.title}
              </h3>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-gray-600 mb-6">
            {currentTourStep.description}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === currentStep ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={handleNext}
              className="btn-primary btn-sm flex items-center gap-2"
            >
              {currentStep === tourSteps.length - 1 ? 'Start Demo' : 'Next'}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default DashboardTour; 